import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { FaRunning, FaChevronDown, FaChevronUp, FaEdit, FaTrash } from 'react-icons/fa';
import api from './services/apiService';
import TrainingCard from './components/training/TrainingCard';
import './scss/training.scss';
import { authService } from './services/authService';
import ProFeatureModal from './components/ProFeatureModal';

const TrainingSchedule = () => {
  const [loading, setLoading] = useState(true);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [viewMode, setViewMode] = useState('team'); // 'my' or 'team'
  const [showProModal, setShowProModal] = useState(false);
  const [showDummyData, setShowDummyData] = useState(false);
  const [repeatSessions, setRepeatSessions] = useState([]);
  const [showRepeatAccordion, setShowRepeatAccordion] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRepeat, setEditingRepeat] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const navigate = useNavigate();
  const teamId = sessionStorage.getItem('selected_team_id');
  const playerId = sessionStorage.getItem('player_id');
  const isManager = authService.isManager();

  // Set default view mode to 'team' for managers
  useEffect(() => {
    if (isManager) {
      setViewMode('team');
    }
  }, [isManager]);

  useEffect(() => {
    const fetchTotalPlayers = async () => {
      try {
        if (!teamId || !isManager) return;
        
        const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
        if (response.data.success) {
          setTotalPlayers(response.data.total_players || 0);
        }
      } catch (error) {
        console.error('Error fetching total players:', error);
      }
    };

    fetchTotalPlayers();
  }, [teamId, isManager]);

  // Dummy training data function
  const getDummyTraining = () => {
    // Create an array of dummy training sessions
    const trainingSessions = [];
    
    // Get current date
    const today = new Date();
    
    // Generate training sessions for the next 4 weeks (Tuesdays and Thursdays)
    for (let week = 0; week < 4; week++) {
      // Tuesday training (day 2 of week, 0-indexed)
      const tuesdayDate = new Date(today);
      tuesdayDate.setDate(today.getDate() + (2 - today.getDay() + 7) % 7 + (week * 7));
      
      // Thursday training (day 4 of week, 0-indexed)
      const thursdayDate = new Date(today);
      thursdayDate.setDate(today.getDate() + (4 - today.getDay() + 7) % 7 + (week * 7));
      
      // Add Tuesday training
      trainingSessions.push({
        training_id: `training-tuesday-${week + 1}`,
        match_id: `training-tuesday-${week + 1}`,
        match_date: format(tuesdayDate, 'yyyy-MM-dd'),
        match_time: '18:00:00',
        time: '18:00:00',
        location: 'Claverham Community College',
        notes: 'Regular Tuesday training session',
        response: 'Invited',
        total_yes: 7,
        total_no: 2,
        total_awaiting: 3,
        duration: 90,
        type: 'training',
        isDummy: true
      });
      
      // Add Thursday training
      trainingSessions.push({
        training_id: `training-thursday-${week + 1}`,
        match_id: `training-thursday-${week + 1}`,
        match_date: format(thursdayDate, 'yyyy-MM-dd'),
        match_time: '18:00:00',
        time: '18:00:00',
        location: 'Claverham Community College',
        notes: 'Regular Thursday training session',
        response: 'Invited',
        total_yes: 8,
        total_no: 1,
        total_awaiting: 2,
        duration: 90,
        type: 'training',
        isDummy: true
      });
    }
    
    return trainingSessions;
  };

  // Fetch repeat training sessions
  const fetchRepeatSessions = useCallback(async () => {
    if (!isManager) return; // Only managers can see repeat sessions
    
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_repeat_training_sessions.php?team_id=${teamId}`);
      const result = response.data;
      
      if (result.success) {
        setRepeatSessions(result.sessions || []);
      } else {
        console.error('Failed to fetch repeat training sessions');
        setRepeatSessions([]);
      }
    } catch (error) {
      console.error('Error fetching repeat training sessions:', error);
      setRepeatSessions([]);
    }
  }, [teamId, isManager]);

  // Define fetchTrainingSessions with useCallback to prevent dependency loop
  const fetchTrainingSessions = useCallback(async () => {
    try {
      setLoading(true);
      // Build the API URL with player_id for responder information
      let apiUrl = `${process.env.REACT_APP_API_URL}/get_training_sessions.php?team_id=${teamId}`;
      
      // Add player_id parameter for responder information
      if (playerId) {
        apiUrl += `&child_id=${playerId}`;
      }
      
      const response = await api.get(apiUrl);
      const result = response.data;
      
      if (result.success) {
        // Process training sessions to ensure they have all required properties
        const processedSessions = result.sessions.map(session => ({
          ...session,
          response: session.response || 'Invited',
          total_yes: session.total_yes || 0,
          total_no: session.total_no || 0,
          response_updated: session.response_updated,
          responder_first_name: session.responder_first_name,
          responder_last_name: session.responder_last_name,
          onRespond: (response, isWaitlist) => handleRespond(session.match_id, response, isWaitlist),
          type: 'training'
        }));
        
        // Filter and show only future sessions
        const now = new Date();
        const futureSessions = processedSessions.filter(session => {
          const sessionDateTime = new Date(session.match_date + ' ' + session.match_time);
          return sessionDateTime > now;
        });
        
        // Sort by date with nearest future sessions first
        const sortedSessions = futureSessions.sort((a, b) => 
          new Date(a.match_date + ' ' + a.match_time) - 
          new Date(b.match_date + ' ' + b.match_time)
        );
        
        // If no real training sessions found, show dummy data
        if (sortedSessions.length === 0) {
          console.log('No training sessions found, showing dummy data');
          const dummySessions = getDummyTraining();
          setTrainingSessions(dummySessions);
          setShowDummyData(true);
        } else {
          setTrainingSessions(sortedSessions);
          setShowDummyData(false);
        }
      } else {
        console.error('Failed to fetch training sessions');
        // Show dummy data on error
        const dummySessions = getDummyTraining();
        setTrainingSessions(dummySessions);
        setShowDummyData(true);
      }
    } catch (error) {
      console.error('Error fetching training sessions:', error);
      // Show dummy data on error
      const dummySessions = getDummyTraining();
      setTrainingSessions(dummySessions);
      setShowDummyData(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]); // Only depends on teamId

  useEffect(() => {
    fetchTrainingSessions();
    fetchRepeatSessions();
  }, [viewMode, playerId, fetchTrainingSessions, fetchRepeatSessions]);

  const handleRespond = async (matchId, response, isWaitlist) => {
    try {
      const playerId = sessionStorage.getItem('player_id');
      const res = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: playerId,
          response: response,
          match_id: matchId,
          is_waitlist: isWaitlist ? 1 : 0
        })
      });

      const data = res.data;
      
      if (data.success) {
        // Update the local state to reflect the change
        setTrainingSessions(prevSessions => 
          prevSessions.map(session => 
            session.match_id === matchId 
              ? { 
                  ...session, 
                  response: response.charAt(0).toUpperCase() + response.slice(1),
                  response_updated: new Date().toISOString(),
                  responder_first_name: sessionStorage.getItem('first_name') || 'Parent',
                  responder_last_name: sessionStorage.getItem('last_name') || ''
                }
              : session
          )
        );
      } else {
        console.error('Failed to update response:', data.message);
      }
    } catch (error) {
      console.error('Error updating response:', error);
    }
  };

  const handleTrainingDeleted = (matchId) => {
    setTrainingSessions(currentSessions => 
      currentSessions.filter(session => session.match_id !== matchId)
    );
  };

  const handleEditRepeat = (repeatSession) => {
    setEditingRepeat(repeatSession);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingRepeat(null);
  };

  const handleSaveRepeat = async (updatedData) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_repeat_training.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repeat_id: editingRepeat.repeat_id,
          ...updatedData
        })
      });

      const result = response.data;
      if (result.success) {
        // Refresh repeat sessions
        fetchRepeatSessions();
        handleCloseEditModal();
      } else {
        console.error('Failed to update repeat training:', result.message);
      }
    } catch (error) {
      console.error('Error updating repeat training:', error);
    }
  };

  const handleDeleteRepeat = async (repeatSession) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this repeat training session?\n\nThis will:\n- Delete the repeat template\n- NOT delete existing individual training sessions\n- Stop future sessions from being created\n\nThis action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/delete_repeat_training.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repeat_id: repeatSession.repeat_id
        })
      });

      const result = response.data;
      if (result.success) {
        // Refresh repeat sessions
        fetchRepeatSessions();
        alert('Repeat training session deleted successfully!');
      } else {
        alert('Failed to delete repeat training session: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting repeat training session:', error);
      alert('An error occurred while deleting the repeat training session');
    }
  };

  if (loading) {
    return <div className="loading">Loading training sessions...</div>;
  }

  // Remove duplicate sessions based on match_id
  const upcomingTrainingSessions = Array.from(new Map(
    trainingSessions.map(session => [session.match_id, session])
  ).values());

  return (
    <div className="matches-container container">
      <Header />
      <h2 className="audiowide">Training Sessions</h2>
      
      {showDummyData && (
        <div className="dummy-data-notice global-notice">
          <h4>Welcome to FirstWhistle!</h4>
          <p>We're showing example training sessions to help you experience the app.
          Your upcoming training sessions will appear here once they've been scheduled by your coach.</p>
        </div>
      )}
      
      {isManager && totalPlayers >= 6 && (
        <div className="manager-actions">
          <button 
            className="add-button"
            onClick={() => navigate('/createTrainingSession')}
          >
            <FaRunning /> Add Training Session
          </button>
        </div>
      )}

      {/* Repeat Training Sessions Accordion - Only for Managers */}
      {isManager && (
        <div className="repeat-sessions-accordion">
          <div 
            className="accordion-header"
            onClick={() => setShowRepeatAccordion(!showRepeatAccordion)}
          >
            <h3>Repeat Training Sessions ({repeatSessions.length})</h3>
            {showRepeatAccordion ? <FaChevronUp /> : <FaChevronDown />}
          </div>
          
          {showRepeatAccordion && (
            <div className="accordion-content">
              {repeatSessions.length > 0 ? (
                <div className="repeat-sessions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Time</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repeatSessions.map((session) => (
                        <tr key={session.repeat_id}>
                          <td>{format(new Date(session.match_date), 'EEEE')}</td>
                          <td>{session.match_time}</td>
                          <td>{session.location}</td>
                          <td>
                            <span className={`status ${session.is_active ? 'active' : 'inactive'}`}>
                              {session.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="edit-button"
                                onClick={() => handleEditRepeat(session)}
                              >
                                <FaEdit /> Edit
                              </button>
                              <button 
                                className="delete-button"
                                onClick={() => handleDeleteRepeat(session)}
                              >
                                <FaTrash /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <p>No repeat training sessions found.</p>
                  <p>Create a training session with "Repeat Weekly" enabled to see it here.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {upcomingTrainingSessions.length > 0 ? (
        <>
          <h2 className="matches-heading">Upcoming Training</h2>
          <div className="matches-list">
            {upcomingTrainingSessions.map((session) => (
              <div key={session.match_id} className="match-wrapper">
                <TrainingCard
                  training={session}
                  onTrainingDeleted={handleTrainingDeleted}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="no-matches">
          <p>No upcoming training sessions scheduled</p>
        </div>
      )}

      <ProFeatureModal
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        featureName="Training History"
      />

      {/* Edit Repeat Training Modal */}
      {showEditModal && editingRepeat && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={handleCloseEditModal}>×</button>
            <h2>Edit Repeat Training Session</h2>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveRepeat({
                  location: formData.get('location'),
                  match_time: formData.get('match_time'),
                  duration: parseInt(formData.get('duration')),
                  instructions: formData.get('instructions'),
                  repeat_end_date: formData.get('repeat_end_date') || null,
                  is_active: formData.get('is_active') === 'on'
                });
              }}>
                <div className="form-group">
                  <label htmlFor="location">Location:</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    defaultValue={editingRepeat.location}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="match_time">Time:</label>
                  <input
                    type="time"
                    id="match_time"
                    name="match_time"
                    defaultValue={editingRepeat.match_time}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="duration">Duration (minutes):</label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    defaultValue={editingRepeat.duration}
                    min="1"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="instructions">Instructions:</label>
                  <textarea
                    id="instructions"
                    name="instructions"
                    defaultValue={editingRepeat.instructions}
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="repeat_end_date">End Date (Optional):</label>
                  <input
                    type="date"
                    id="repeat_end_date"
                    name="repeat_end_date"
                    defaultValue={editingRepeat.repeat_end_date || ''}
                  />
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      defaultChecked={editingRepeat.is_active}
                    />
                    Active (uncheck to disable this repeat series)
                  </label>
                </div>
                
                <div className="modal-buttons">
                  <button type="button" onClick={handleCloseEditModal} className="cancel">
                    Cancel
                  </button>
                  <button type="submit">
                    Save Changes
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSchedule; 